import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Info, Loader2, Users, Send, CheckCircle2, Eye, MousePointerClick, AlertTriangle, ShieldAlert, UserMinus, X } from "lucide-react";

/**
 * AdminAnalytics — /admin/analytics (label "Email marketing" na sidebar).
 *
 * Página de consulta apenas. Sem funções de envio.
 * Métricas alimentadas por webhooks Resend (Svix). Envios de teste são
 * sempre excluídos (`is_test=true` tag).
 *
 * As Aberturas são identificadas como "indicativas" — Apple Mail Privacy
 * Protection e Gmail prefetch abrem imagens sem interacção humana.
 */

const RANGES = [
    { key: "7d", label: "7d" },
    { key: "30d", label: "30d" },
    { key: "90d", label: "90d" },
    { key: "all", label: "Tudo" },
];

const TYPES = [
    { key: "all", label: "Todas" },
    { key: "newsletter", label: "Nota de Obra" },
    { key: "blog_article", label: "NOVO NO BLOG" },
];

const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("pt-PT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
};

const nFmt = new Intl.NumberFormat("pt-PT");

export default function AdminAnalytics() {
    const [range, setRange] = useState("30d");
    const [ctype, setCtype] = useState("all");
    const [summary, setSummary] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, c] = await Promise.all([
                api.get(`/admin/email-analytics/summary`, { params: { range, campaign_type: ctype } }),
                api.get(`/admin/email-analytics/campaigns`, { params: { range, campaign_type: ctype, limit: 100 } }),
            ]);
            setSummary(s.data);
            setCampaigns(c.data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Não foi possível carregar analytics");
        } finally { setLoading(false); }
    }, [range, ctype]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="max-w-6xl">
            <h1 className="font-display text-3xl font-bold text-stone-900 flex items-center gap-2 mb-1" data-testid="analytics-title">
                <BarChart3 className="w-6 h-6 text-[hsl(var(--copper))]" /> Email marketing
            </h1>
            <p className="text-sm text-stone-500 mb-6">Métricas de entrega, aberturas, cliques e cancelamentos das campanhas enviadas pelo Resend. Apenas consulta.</p>

            {/* Filtros */}
            <div className="flex flex-wrap gap-3 mb-6" data-testid="analytics-filters">
                <FilterGroup label="Intervalo" value={range} onChange={setRange} options={RANGES} testid="analytics-range" />
                <FilterGroup label="Tipo de campanha" value={ctype} onChange={setCtype} options={TYPES} testid="analytics-type" />
            </div>

            {/* Tiles globais */}
            <SummaryTiles summary={summary} loading={loading} />

            {/* Tabela */}
            <div className="mt-8">
                <h2 className="font-display text-lg font-bold text-stone-900 mb-3">Campanhas</h2>
                <CampaignsTable campaigns={campaigns} loading={loading} onOpen={setSelected} />
            </div>

            {selected && <CampaignDetailModal campaign={selected} onClose={() => setSelected(null)} />}
        </div>
    );
}

// ─── Filter chips ────────────────────────────────────────────────────

function FilterGroup({ label, value, onChange, options, testid }) {
    return (
        <div className="flex items-center gap-2" data-testid={testid}>
            <span className="text-xs uppercase tracking-widest text-stone-500 font-mono">{label}</span>
            <div className="inline-flex rounded-full border border-stone-200 bg-white p-0.5">
                {options.map((o) => (
                    <button key={o.key} onClick={() => onChange(o.key)}
                        data-testid={`${testid}-${o.key}`}
                        className={`text-xs px-3 py-1.5 rounded-full transition ${
                            value === o.key
                                ? "bg-[hsl(var(--forest))] text-white font-semibold"
                                : "text-stone-600 hover:text-stone-900"
                        }`}>
                        {o.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

// ─── Global summary tiles ────────────────────────────────────────────

function SummaryTiles({ summary, loading }) {
    if (loading && !summary) return <div className="text-stone-500 flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> A carregar…</div>;
    if (!summary) return null;

    const s = summary;
    return (
        <div data-testid="analytics-summary">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Tile testid="tile-active-subs" icon={Users} label="Subscritores activos" value={nFmt.format(s.active_subscribers)} tone="forest" />
                <Tile testid="tile-sent" icon={Send} label="Enviados" value={nFmt.format(s.sent)} tone="stone" />
                <Tile testid="tile-delivered" icon={CheckCircle2} label="Entregues" value={nFmt.format(s.delivered)} pct={s.delivered_pct} tone="emerald" />
                <Tile testid="tile-opened" icon={Eye} label="Aberturas" value={nFmt.format(s.opened)} pct={s.opened_pct} tone="copper" indicative />
                <Tile testid="tile-clicked" icon={MousePointerClick} label="Cliques" value={nFmt.format(s.clicked)} pct={s.clicked_pct} tone="copper" />
                <Tile testid="tile-bounced" icon={AlertTriangle} label="Bounces" value={nFmt.format(s.bounced)} pct={s.bounced_pct} tone="amber" />
                <Tile testid="tile-complained" icon={ShieldAlert} label="Queixas" value={nFmt.format(s.complained)} pct={s.complained_pct} tone="red" />
                <Tile testid="tile-unsubscribed" icon={UserMinus} label="Unsubscribes" value={nFmt.format(s.unsubscribed)} tone="stone" />
            </div>
            <div className="mt-3 flex items-start gap-2 text-[11px] text-stone-500 leading-relaxed" data-testid="analytics-privacy-note">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-stone-400" />
                <span>Aberturas são <b>indicativas</b>. Apple Mail Privacy Protection e Gmail prefetch abrem imagens sem interacção humana, inflacionando esta métrica. Confie mais em <b>Cliques</b> para engagement real. Envios de teste estão sempre excluídos.</span>
            </div>
        </div>
    );
}

const TONE = {
    forest: "bg-[hsl(var(--forest))] text-white",
    copper: "bg-[hsl(var(--copper))] text-white",
    emerald: "bg-emerald-600 text-white",
    stone: "bg-stone-900 text-white",
    amber: "bg-amber-500 text-white",
    red: "bg-red-600 text-white",
};

function Tile({ icon: Icon, label, value, pct, tone = "stone", indicative = false, testid }) {
    return (
        <div className="bg-white rounded-xl border border-stone-200 p-4 relative overflow-hidden" data-testid={testid}>
            <div className={`inline-flex items-center gap-1.5 rounded-full ${TONE[tone]} text-[10px] uppercase tracking-widest font-mono px-2 py-0.5 mb-2`}>
                <Icon className="w-3 h-3" /> {label}
            </div>
            <div className="flex items-baseline gap-2">
                <span className="text-2xl font-display font-bold text-stone-900">{value}</span>
                {typeof pct === "number" && (
                    <span className="text-sm text-stone-500 font-mono">{pct.toFixed(1)}%</span>
                )}
            </div>
            {indicative && (
                <div className="mt-1 text-[10px] text-stone-400 uppercase tracking-widest font-mono">Indicativo</div>
            )}
        </div>
    );
}

// ─── Campaigns table ─────────────────────────────────────────────────

const TYPE_PILL = {
    newsletter: { label: "Nota de Obra", cls: "bg-[hsl(var(--forest))] text-white" },
    blog_article: { label: "NOVO NO BLOG", cls: "bg-[hsl(var(--copper))] text-white" },
};

function CampaignsTable({ campaigns, loading, onOpen }) {
    if (loading && !campaigns.length) {
        return <div className="text-stone-500 flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> A carregar…</div>;
    }
    if (!campaigns.length) {
        return <div className="p-10 text-center text-stone-500 bg-white rounded-xl border border-stone-200" data-testid="analytics-campaigns-empty">Sem campanhas neste intervalo/tipo.</div>;
    }
    return (
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden" data-testid="analytics-campaigns">
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-stone-50 text-[10px] uppercase tracking-widest text-stone-500 font-mono">
                        <tr>
                            <th className="text-left px-4 py-3">Campanha</th>
                            <th className="text-right px-3 py-3">Enviados</th>
                            <th className="text-right px-3 py-3">Entregues</th>
                            <th className="text-right px-3 py-3">Aberturas</th>
                            <th className="text-right px-3 py-3">Cliques</th>
                            <th className="text-right px-3 py-3">Bounces</th>
                            <th className="text-right px-3 py-3">Unsubs</th>
                            <th className="px-3 py-3"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-stone-100">
                        {campaigns.map((c) => (
                            <CampaignRow key={c.id} c={c} onOpen={onOpen} />
                        ))}
                    </tbody>
                </table>
            </div>
            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-stone-100">
                {campaigns.map((c) => (
                    <CampaignCardMobile key={c.id} c={c} onOpen={onOpen} />
                ))}
            </div>
        </div>
    );
}

function CampaignRow({ c, onOpen }) {
    const a = c.analytics || {};
    const t = TYPE_PILL[c.campaign_type] || { label: c.campaign_type || "—", cls: "bg-stone-200 text-stone-700" };
    return (
        <tr className="hover:bg-stone-50/60 transition" data-testid={`analytics-row-${c.id}`}>
            <td className="px-4 py-3">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] uppercase tracking-widest font-mono rounded-full px-2 py-0.5 ${t.cls}`}>{t.label}</span>
                    <span className="font-medium text-stone-900 truncate max-w-md">{c.subject}</span>
                </div>
                <div className="text-[11px] text-stone-500 mt-1">{fmtDate(c.created_at)}</div>
            </td>
            <MetricCell value={a.sent} />
            <MetricCell value={a.delivered} pct={a.delivered_pct} />
            <MetricCell value={a.opened} pct={a.opened_pct} indicative />
            <MetricCell value={a.clicked} pct={a.clicked_pct} />
            <MetricCell value={a.bounced} pct={a.bounced_pct} muted={!a.bounced} />
            <MetricCell value={a.unsubscribed} muted={!a.unsubscribed} />
            <td className="px-3 py-3 text-right">
                <button onClick={() => onOpen(c)} className="text-xs px-3 py-1.5 border border-stone-300 rounded" data-testid={`analytics-open-${c.id}`}>Ver</button>
            </td>
        </tr>
    );
}

function MetricCell({ value = 0, pct, indicative = false, muted = false }) {
    return (
        <td className={`px-3 py-3 text-right font-mono tabular-nums ${muted ? "text-stone-400" : "text-stone-900"}`}>
            <div className="text-sm font-semibold">{nFmt.format(value)}</div>
            {typeof pct === "number" && (
                <div className={`text-[11px] ${muted ? "text-stone-400" : "text-stone-500"}`}>
                    {pct.toFixed(1)}%{indicative ? " ·" : ""}
                    {indicative && <span className="ml-0.5 text-stone-400">ind.</span>}
                </div>
            )}
        </td>
    );
}

function CampaignCardMobile({ c, onOpen }) {
    const a = c.analytics || {};
    const t = TYPE_PILL[c.campaign_type] || { label: c.campaign_type || "—", cls: "bg-stone-200 text-stone-700" };
    return (
        <button onClick={() => onOpen(c)} className="w-full text-left p-4 hover:bg-stone-50/60 transition" data-testid={`analytics-card-${c.id}`}>
            <div className="flex items-center gap-2 flex-wrap mb-1">
                <span className={`text-[10px] uppercase tracking-widest font-mono rounded-full px-2 py-0.5 ${t.cls}`}>{t.label}</span>
                <span className="text-[11px] text-stone-500">{fmtDate(c.created_at)}</span>
            </div>
            <div className="font-medium text-stone-900 mb-2">{c.subject}</div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
                <MiniMetric label="Enviados" value={a.sent} />
                <MiniMetric label="Entregues" value={a.delivered} pct={a.delivered_pct} />
                <MiniMetric label="Aberturas" value={a.opened} pct={a.opened_pct} indicative />
                <MiniMetric label="Cliques" value={a.clicked} pct={a.clicked_pct} />
                <MiniMetric label="Bounces" value={a.bounced} />
                <MiniMetric label="Unsubs" value={a.unsubscribed} />
            </div>
        </button>
    );
}

function MiniMetric({ label, value = 0, pct, indicative = false }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-mono">{label}{indicative ? " (ind.)" : ""}</div>
            <div className="text-stone-900 font-semibold font-mono">{nFmt.format(value)}{typeof pct === "number" ? <span className="text-stone-500 font-normal ml-1">{pct.toFixed(1)}%</span> : null}</div>
        </div>
    );
}

// ─── Drill-down modal ────────────────────────────────────────────────

function CampaignDetailModal({ campaign, onClose }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        api.get(`/admin/email-analytics/campaigns/${campaign.id}`)
            .then((r) => { if (!cancelled) setDetail(r.data); })
            .catch(() => { if (!cancelled) toast.error("Erro ao carregar detalhe"); })
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [campaign.id]);

    const a = detail?.analytics || campaign.analytics || {};
    const t = TYPE_PILL[campaign.campaign_type] || { label: campaign.campaign_type || "—", cls: "bg-stone-200 text-stone-700" };

    return (
        <div className="fixed inset-0 bg-stone-950/60 z-50 overflow-y-auto" onClick={onClose} data-testid="analytics-detail-modal">
            <div className="min-h-full flex items-start justify-center p-4">
                <div className="bg-white rounded-xl max-w-3xl w-full my-8" onClick={(e) => e.stopPropagation()}>
                    <div className="p-6 space-y-4">
                        <div className="flex items-start gap-3">
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className={`text-[10px] uppercase tracking-widest font-mono rounded-full px-2 py-0.5 ${t.cls}`}>{t.label}</span>
                                    <span className="text-[11px] text-stone-500">{fmtDate(campaign.created_at)}</span>
                                </div>
                                <h2 className="font-display font-bold text-xl text-stone-900">{campaign.subject}</h2>
                            </div>
                            <button onClick={onClose} className="text-stone-500 hover:text-stone-900" data-testid="analytics-detail-close">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        {loading && !detail ? (
                            <div className="text-stone-500 flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> A carregar…</div>
                        ) : (
                            <>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <Tile icon={Send} label="Enviados" value={nFmt.format(a.sent || 0)} tone="stone" />
                                    <Tile icon={CheckCircle2} label="Entregues" value={nFmt.format(a.delivered || 0)} pct={a.delivered_pct} tone="emerald" />
                                    <Tile icon={Eye} label="Aberturas" value={nFmt.format(a.opened || 0)} pct={a.opened_pct} tone="copper" indicative />
                                    <Tile icon={MousePointerClick} label="Cliques" value={nFmt.format(a.clicked || 0)} pct={a.clicked_pct} tone="copper" />
                                    <Tile icon={AlertTriangle} label="Bounces" value={nFmt.format(a.bounced || 0)} pct={a.bounced_pct} tone="amber" />
                                    <Tile icon={ShieldAlert} label="Queixas" value={nFmt.format(a.complained || 0)} pct={a.complained_pct} tone="red" />
                                    <Tile icon={UserMinus} label="Unsubscribes" value={nFmt.format(a.unsubscribed || 0)} tone="stone" />
                                </div>
                                <div className="text-xs text-stone-500 flex flex-wrap gap-3 pt-2 border-t border-stone-100">
                                    <span>Destinatários: <b>{nFmt.format(detail?.recipient_count || 0)}</b></span>
                                    <span>Estado: <b>{detail?.status || "—"}</b></span>
                                    {detail?.sent_at && <span>Concluída: <b>{fmtDate(detail.sent_at)}</b></span>}
                                    {detail?.failed_count > 0 && <span className="text-red-600">Falhas: <b>{detail.failed_count}</b></span>}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
